import { useState, useMemo, useCallback } from "react";
import { format } from "date-fns";
import { SUPPORT_MONKEY_UID } from "../constants/supportConstants";

interface ChatListFilterOptions {
  myChats: any[];
  myAssociates: any[];
  jobFilteringReady: boolean;
  allowedJobIdsUnion: Set<string>;
  cancelledBySchedulerWerker: Record<string, boolean>;
  cancelledBySchedulerScheduler: Record<string, boolean>;
}

interface UseChatListFilterResult {
  filteredChats: any[];
  searchText: string;
  setSearchText: (text: string) => void;
}

/**
 * Hook to manage chat list filtering and sorting.
 *
 * Features:
 * - Search filtering by chat title, preview, or type ("job"/"dm")
 * - Filter out unstarted chats ("chat created" or empty preview)
 * - Always allow WerkApp Support chats
 * - Filter normal DMs by associate list
 * - Filter job chats by allowed job IDs and cancellation status
 * - Sort by unread status first, then by date (newest first)
 * - Add metadata fields to each chat
 */
export function useChatListFilter(
  options: ChatListFilterOptions
): UseChatListFilterResult {
  const {
    myChats,
    myAssociates,
    jobFilteringReady,
    allowedJobIdsUnion,
    cancelledBySchedulerWerker,
    cancelledBySchedulerScheduler,
  } = options;

  const [searchText, setSearchText] = useState("");

  const isUnstartedChat = useCallback((preview: any) => {
    const normalizedPreview = String(preview || "")
      .trim()
      .toLowerCase();

    return (
      normalizedPreview === "chat created" ||
      normalizedPreview === ""
    );
  }, []);

  const isJobChat = useCallback((msg: any) => {
    return (
      typeof msg?.DMuid === "string" &&
      msg.DMuid.trim() === ""
    );
  }, []);

  const isDmChat = useCallback((msg: any) => {
    return (
      typeof msg?.DMuid === "string" &&
      msg.DMuid.trim() !== ""
    );
  }, []);

  const associateUidSet = useMemo(() => {
    const set = new Set<string>();

    (myAssociates || []).forEach((associate: any) => {
      if (associate?.WerkerUid) {
        set.add(String(associate.WerkerUid));
      }
    });

    return set;
  }, [myAssociates]);

  const filteredChats = useMemo(() => {
    const queryText = String(searchText || "")
      .trim()
      .toLowerCase();

    return (myChats || [])
      .filter((msg: any) => {
        const key = String(msg?.Key || "");

        if (!msg || !key) {
          return false;
        }

        const preview = String(msg?.MessagePreview || "");

        if (isUnstartedChat(preview)) {
          return false;
        }

        const dmuid = String(msg?.DMuid ?? "");
        const isSupportChat = dmuid === SUPPORT_MONKEY_UID;

        if (isDmChat(msg)) {
          // Normal DMs must belong to an associate.
          // WerkApp Support is always allowed in the list.
          if (!isSupportChat && !associateUidSet.has(dmuid)) {
            return false;
          }
        }

        if (isJobChat(msg)) {
          if (!jobFilteringReady) {
            return false;
          }

          const jobId = key;

          if (!allowedJobIdsUnion.has(jobId)) {
            return false;
          }

          const isRead = msg?.IsRead === true;

          const cancelled =
            cancelledBySchedulerWerker?.[jobId] === true ||
            cancelledBySchedulerScheduler?.[jobId] === true;

          // Hide cancelled job chats after they have been read.
          if (cancelled && isRead) {
            return false;
          }
        }

        if (!queryText) {
          return true;
        }

        const title = String(msg?.MessageTitle || "").toLowerCase();
        const messagePreview = String(
          msg?.MessagePreview || ""
        ).toLowerCase();

        const kindToken = isJobChat(msg) ? "job" : "dm";

        return (
          title.includes(queryText) ||
          messagePreview.includes(queryText) ||
          kindToken.includes(queryText)
        );
      })
      .sort((a: any, b: any) => {
        const aUnread = a?.IsRead === false ? 1 : 0;
        const bUnread = b?.IsRead === false ? 1 : 0;

        if (aUnread !== bUnread) {
          return bUnread - aUnread;
        }

        const aDate = Number(a?.MessageDate) || 0;
        const bDate = Number(b?.MessageDate) || 0;

        return bDate - aDate;
      })
      .map((msg: any) => {
        const preview = String(msg?.MessagePreview || "");
        const dateMs = Number(msg?.MessageDate) || 0;
        const kind = isJobChat(msg) ? "JOB" : "DM";

        const displayDate = dateMs
          ? format(new Date(dateMs), "M/d/yy h:mm a")
          : "";

        const avatarUrl =
          msg?.SendersPic ||
          msg?.SendersPicURL ||
          msg?.sendersPic ||
          msg?.SendersPicUrl ||
          "";

        return {
          ...msg,
          _kind: kind,
          _displayPreview: preview,
          _dateMs: dateMs,
          _displayDate: displayDate,
          _avatarUrl: avatarUrl,
        };
      });
  }, [
    myChats,
    searchText,
    isUnstartedChat,
    isJobChat,
    isDmChat,
    associateUidSet,
    jobFilteringReady,
    allowedJobIdsUnion,
    cancelledBySchedulerWerker,
    cancelledBySchedulerScheduler,
  ]);

  return {
    filteredChats,
    searchText,
    setSearchText,
  };
}